import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { bridge } from '@/lib/bridge';
import { brandsApi } from '@/services/brands';
import { Brand, ParkId, ParkUri } from '@/types';

import { RootState } from '../store';

export const invalidate = createAction('app/invalidate');

export const syncNativeAppContext = createAsyncThunk('app/syncNativeAppContext', async () => {
  const result = await bridge.getInitialBridgeContext();

  console.info(`%c*** Bridge Context *** %O`, 'color: purple; font-size: 16px', result);

  return {
    brand: result.brand || Brand.SeaWorld,
    parkId: result.parkId || 'AC3AF402-3C62-4893-8B05-822F19B9D2BC',
    token: result.token,
    clientPrivateKeyPem: result.clientPrivateKeyPem,
    driftInSeconds: result.driftInSeconds,
    isInPark: result.isInPark,
    lat: result.lat,
    lng: result.lng,
    featureFlagUser: result.featureFlagUser,
  };
});

export interface AppState {
  brand: Brand | null;
  parkId: ParkId | null;
  parkUri: ParkUri | null;
  isInPark: boolean;
  lat: number;
  lng: number;
}

const initialState: AppState = {
  brand: null,
  parkId: null,
  parkUri: null,
  isInPark: false,
  lat: 0.0,
  lng: 0.0,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(syncNativeAppContext.fulfilled, (state, { payload }) => {
        state.brand = payload.brand;
        state.parkId = payload.parkId;
        state.isInPark = payload.isInPark;
        state.lat = payload.lat ?? 0.0;
        state.lng = payload.lng ?? 0.0;
      })
      .addMatcher(brandsApi.endpoints.getBrand.matchFulfilled, (state, { payload }) => {
        const park = payload.parks?.find((park) => park.id === state.parkId);
        state.parkUri = park?.uriId || '';
      });
  },
});

export default appSlice.reducer;

export const getAppState = (state: RootState) => state.app;

export const getBrand = (state: RootState) => state.app.brand;

export const getParkId = (state: RootState) => state.app.parkId;

export const getParkUri = (state: RootState) => state.app.parkUri;

export const getIsInPark = (state: RootState) => state.app.isInPark;

export const getLat = (state: RootState) => state.app.lat;

export const getLng = (state: RootState) => state.app.lng;
